// Persistent founder actions. Not a task list: commercial execution and
// evidence accountability. Dates and Top 3 selection are derived; records
// stay open until Completed, Cancelled, Parked or Superseded.

import {
  ACTION_CLOSED_STATUSES,
  ACTION_OPEN_STATUSES,
  EVIDENCE_BY_ID,
} from './constants';
import { isAfter, diffDays } from './dates';
import { PRIORITY_CONFIG_V1, scoringConfigVersion } from './scoring';
import { isOutOfScopeAction } from './evidence';
import { INITIALIZATION_PACKAGE_IDS } from './workPackages';

export function isOpenAction(a) {
  return ACTION_OPEN_STATUSES.includes(a?.status);
}

export function isClosedAction(a) {
  return ACTION_CLOSED_STATUSES.includes(a?.status);
}

export function evidenceWeight(level) {
  if (!level || level === 'OPS') return 0;
  return EVIDENCE_BY_ID[level]?.weight ?? 0;
}

const PRIORITY_SCORE = { Critical: 40, High: 24, Medium: 10, Low: 2 };

const LEGACY_STATUS = {
  Open: 'Ready',
  'In Progress': 'In progress',
  Completed: 'Closed',
  Parked: 'Closed',
  Cancelled: 'Closed',
  Superseded: 'Closed',
};

export function migrateMoveStatus(status, selectedForToday) {
  if (selectedForToday && (status === 'Open' || status === 'Ready' || status === 'Backlog')) return 'Today';
  return LEGACY_STATUS[status] || status || 'Ready';
}

export function newAction(partial = {}, today) {
  const selected = !!partial.selectedForToday;
  const status = migrateMoveStatus(partial.status || (selected ? 'Today' : 'Ready'), selected);
  return {
    id: partial.id,
    text: String(partial.text || '').trim(),
    workstream: partial.workstream || 'Dubai',
    priority: partial.priority || 'Medium',
    expectedEvidence: partial.expectedEvidence || 'E3',
    dueDate: partial.dueDate || today || '',
    owner: partial.owner || 'Founder',
    status,
    gateRelevance: partial.gateRelevance || 'none',
    linkedKind: partial.linkedKind || '',
    linkedId: partial.linkedId || '',
    linkedLabel: partial.linkedLabel || '',
    blocker: partial.blocker || '',
    selectedForToday: selected || status === 'Today',
    todayRank: partial.todayRank || 0,
    date: partial.date || today || '',
    createdAt: partial.createdAt || today || '',
    completedAt: partial.completedAt || '',
    closedAt: partial.closedAt || '',
    completionResult: partial.completionResult || partial.outcome || '',
    evidenceCreated: partial.evidenceCreated || '',
    nextActionText: partial.nextActionText || '',
    supersededBy: partial.supersededBy || '',
    outcome: partial.outcome || partial.completionResult || '',
    outcomeNote: partial.outcomeNote || '',
    workPackageId: partial.workPackageId || '',
    epicId: partial.epicId || '',
    gateOutcomeKey: partial.gateOutcomeKey || '',
    gateOutcomeId: partial.gateOutcomeId || '',
    completionCondition: partial.completionCondition || '',
    effortEstimate: partial.effortEstimate || '',
    source: partial.source || 'Manual',
    disposition: partial.disposition || '',
    evidenceRecordIds: partial.evidenceRecordIds || [],
    whyNow: partial.whyNow || '',
    evidenceCredit: partial.evidenceCredit || (partial.expectedEvidence === 'OPS' ? 'none' : ''),
    aiPlanRank: partial.aiPlanRank || 0,
    idempotencyKey: partial.idempotencyKey || '',
    preparation: !!partial.preparation || !!partial.bridgeCreated,
    bridgeCreated: !!partial.bridgeCreated || partial.source === 'Bridge',
    expectedResult: partial.expectedResult || '',
    accountIds: partial.accountIds || [],
  };
}

/** Lift a pre-v3 date-stamped top3 row into a persistent action. */
export function migrateAction(row) {
  const t = row || {};
  if (t.status && ACTION_OPEN_STATUSES.concat(ACTION_CLOSED_STATUSES).includes(t.status)) {
    const selected = !!t.selectedForToday || t.status === 'Today';
    return {
      ...newAction(t, t.date || t.createdAt),
      ...t,
      status: migrateMoveStatus(t.status, selected),
      selectedForToday: selected,
      completionResult: t.completionResult || t.outcome || '',
      outcome: t.outcome || t.completionResult || '',
      source: t.source || 'Manual',
      workPackageId: t.workPackageId || '',
      disposition: t.disposition || dispositionFromLegacy(t.status),
      evidenceRecordIds: t.evidenceRecordIds || [],
    };
  }
  const closed = !!t.outcome;
  return newAction(
    {
      ...t,
      status: closed ? 'Closed' : 'Ready',
      completionResult: t.outcome || '',
      evidenceCreated: t.outcome === 'yes' ? t.expectedEvidence : t.outcome === 'partial' ? t.expectedEvidence : '',
      selectedForToday: false,
      completedAt: closed ? t.date || '' : '',
      closedAt: closed ? t.date || '' : '',
      disposition: closed ? dispositionFromLegacy('Completed') : '',
    },
    t.date,
  );
}

function dispositionFromLegacy(status) {
  if (status === 'Parked' || status === 'Cancelled' || status === 'Superseded') return 'stopped';
  if (status === 'Completed') return 'learning';
  return '';
}

export function scoreAction(a, ctx) {
  return scoreMove(a, ctx).total;
}

export function scoreMove(a, ctx, config = PRIORITY_CONFIG_V1) {
  const { sprint, funnel, constraintTitle, time } = ctx || {};
  const w = config.weights || PRIORITY_CONFIG_V1.weights;
  const p = config.penalties || PRIORITY_CONFIG_V1.penalties;
  const nextGate = sprint?.nextGate?.day;
  const parts = {
    constraint: 0,
    gate: 0,
    evidenceUplift: 0,
    founderOnly: 0,
    dueAge: 0,
    allocationDrift: 0,
  };

  const blob = `${a.text || ''} ${a.whyNow || ''} ${a.linkedLabel || ''}`.toLowerCase();
  if (constraintTitle && blob.includes(String(constraintTitle).slice(0, 18).toLowerCase())) {
    parts.constraint = w.constraint;
  } else if (a.linkedKind === 'blocker' || a.workstream === 'D001') {
    parts.constraint = Math.round(w.constraint * 0.6);
  }

  if (nextGate && String(a.gateRelevance) === String(nextGate)) parts.gate = w.gate;
  else if (a.gateRelevance && a.gateRelevance !== 'none') parts.gate = Math.round(w.gate * 0.4);

  parts.evidenceUplift = Math.round((evidenceWeight(a.expectedEvidence) / 5) * w.evidenceUplift);

  if (a.owner === 'Founder' || !a.owner) parts.founderOnly = w.founderOnly;

  if (a.dueDate && sprint?.executionAsOf && isAfter(sprint.executionAsOf, a.dueDate)) parts.dueAge = w.dueAge;
  else if (a.dueDate && sprint?.executionAsOf) {
    const age = Math.abs(diffDays(sprint.executionAsOf, a.dueDate));
    if (age <= 2) parts.dueAge = Math.round(w.dueAge * 0.5);
  }

  if (time?.drifting && (a.workstream === 'Dubai' || a.workstream === 'D001')) {
    parts.allocationDrift = w.allocationDrift;
  }

  let penalty = 0;
  if (a.expectedEvidence === 'OPS' && !a.linkedId) penalty += p.unlinkedInternal;
  if (evidenceWeight(a.expectedEvidence) >= 4 && !a.linkedId) penalty += p.missingData;
  if (a.workstream === 'Other' && !a.linkedId) penalty += p.productWithoutOpp;
  if (!String(a.completionCondition || a.text || '').trim()) penalty += p.missingData;
  if (a.status === 'Blocked') penalty += p.blocked;
  if (isOutOfScopeAction(a, ctx?.exceptions)) penalty += p.outOfScope;

  const total = Object.values(parts).reduce((s, n) => s + n, 0) - penalty;
  return {
    total,
    parts,
    penalty,
    scoringConfigVersion: scoringConfigVersion(config),
  };
}

export function evaluateEligibility(a, ctx = {}) {
  const reasons = [];
  if (isOutOfScopeAction(a, ctx.exceptions)) reasons.push('out-of-scope');
  if (a.status === 'Blocked' && !String(a.blocker || '').trim()) reasons.push('unnamed-blocker');
  if (!String(a.owner || '').trim()) reasons.push('no-owner');
  if (!String(a.text || '').trim()) reasons.push('no-completion-condition');
  const waiting = commercialWaiting(a, ctx.state);
  if (waiting) reasons.push('waiting-external');
  return { eligible: reasons.length === 0, reasons };
}

function commercialWaiting(a, state) {
  if (!a?.linkedId || !state) return false;
  if (a.linkedKind === 'account') {
    const acc = (state.accounts || []).find((x) => x.id === a.linkedId);
    return !!(acc && acc.waitingExternal);
  }
  const collections = [
    ...(state.opportunities || []),
    ...(state.d001?.partners || []),
    ...(state.assetCandidates || []),
    ...(state.infra?.opportunities || []),
  ];
  const rec = collections.find((x) => x.id === a.linkedId);
  return !!(rec && rec.waitingExternal);
}

/** Today strip: accepted / pinned moves only. Never auto-fill. */
export function pickToday(open, ctx) {
  return open
    .filter((a) => a.selectedForToday || a.status === 'Today')
    .sort((a, b) => (a.todayRank || 99) - (b.todayRank || 99) || scoreAction(b, ctx) - scoreAction(a, ctx))
    .slice(0, 3)
    .map((a) => ({ ...a, autoSelected: false, source: a.source || 'Manual' }));
}

export function isInitializationMove(a, state) {
  if (!a) return false;
  if (INITIALIZATION_PACKAGE_IDS.includes(a.workPackageId)) return true;
  const pkg = (state?.workPackages || []).find((p) => p.id === a.workPackageId);
  return !!pkg?.setup;
}

export function evaluateManualMove(partial = {}, ctx = {}) {
  const reasons = [];
  const linkedKind = String(partial.linkedKind || '').trim();
  const linkedId = String(partial.linkedId || '').trim();
  const blocker = String(partial.blocker || '').trim();
  const linked =
    (linkedKind === 'blocker' && !!blocker) ||
    (linkedKind && linkedKind !== 'blocker' && !!linkedId);
  const why = String(partial.whyNow || '').trim();
  const outcome = String(partial.completionCondition || partial.expectedEvidence || '').trim();
  const due = String(partial.dueDate || '').trim();
  if (!linked) reasons.push('unlinked');
  if (!why) reasons.push('no-why');
  if (!outcome) reasons.push('no-outcome');
  if (!due) reasons.push('no-due');
  if (isOutOfScopeAction(partial, ctx.exceptions)) reasons.push('out-of-scope');
  const challenge = reasons.includes('unlinked') || reasons.includes('out-of-scope');
  return {
    ok: reasons.length === 0,
    challenge,
    reasons,
    message: challenge
      ? 'This move is not linked to a current gate, blocker, or commercial object. Add anyway / Park / Cancel.'
      : '',
  };
}

/** Governor ranking of Ready/Backlog moves that are not already Today. */
export function recommendReadyMoves(open, ctx, config = PRIORITY_CONFIG_V1) {
  return applySelectionGuardrails(rankEligibleMoves(open, ctx, config)).map((a, i) => ({
    ...a,
    rank: i + 1,
    scoringConfigVersion: scoringConfigVersion(config),
  }));
}

/** Ranked Ready Next lane — up to five executable commercial moves after Today. */
export function rankReadyNext(open, ctx, limit = 5, config = PRIORITY_CONFIG_V1) {
  return rankEligibleMoves(open, ctx, config).slice(0, limit).map((a, i) => ({
    ...a,
    rank: i + 1,
    scoringConfigVersion: scoringConfigVersion(config),
  }));
}

function rankEligibleMoves(open, ctx, config = PRIORITY_CONFIG_V1) {
  const eligible = [];
  for (const a of open || []) {
    if (a.selectedForToday || a.status === 'Today') continue;
    if (a.status === 'Blocked') continue;
    if (isInitializationMove(a, ctx?.state)) continue;
    const ev = evaluateEligibility(a, ctx);
    if (!ev.eligible) continue;
    const scored = scoreMove(a, ctx, config);
    eligible.push({ ...a, ...scored, eligible: true });
  }
  eligible.sort((a, b) => b.total - a.total || String(a.id).localeCompare(String(b.id)));
  return eligible;
}

export function waitingExternalObjects(state) {
  const out = [];
  const push = (kind, rec, label) => {
    if (!rec?.waitingExternal) return;
    out.push({
      kind,
      id: rec.id,
      label: label || rec.id,
      waitingUntil: rec.waitingUntil || rec.nextActionDue || '',
      waitingFor: rec.waitingFor || rec.nextAction || '',
      noResponseFlag: !!rec.noResponseFlag,
    });
  };
  for (const a of state?.accounts || []) {
    push('account', a, a.name);
  }
  for (const o of state?.opportunities || []) {
    push('opportunity', o, o.name || o.accountName);
  }
  for (const p of state?.d001?.partners || []) {
    push('partner', p, p.org || p.name);
  }
  for (const a of state?.assetCandidates || []) {
    push('asset', a, a.name);
  }
  for (const o of state?.infra?.opportunities || []) {
    push('infra', o, o.account || o.name);
  }
  out.sort((a, b) => String(a.waitingUntil || '9999').localeCompare(String(b.waitingUntil || '9999')));
  return out;
}

const RECOGNIZED_CLOCK = /confirm sprint clock/i;
const RECOGNIZED_UNIVERSE = /(?:complete|build) (the )?(dubai )?(target )?universe/i;
const RECOGNIZED_VIRIAL = /\b(virialai|viral ai|social media presence)\b/i;

function isRecognizedSeedMove(a) {
  return String(a?.id || '').startsWith('wp-move-') || !!a?.workPackageId;
}

function closeRecognized(a, today, disposition, note) {
  return {
    ...a,
    status: 'Closed',
    selectedForToday: false,
    todayRank: 0,
    aiPlanRank: 0,
    closedAt: a.closedAt || today || '',
    completedAt: a.completedAt || today || '',
    disposition,
    completionResult: disposition === 'stopped' ? 'no' : 'operational',
    outcomeNote: a.outcomeNote || note,
  };
}

/**
 * Close recognized seed contamination without deleting founder-entered moves.
 * Clock is already complete; VirialAI is out of scope; universe is a work package.
 */
export function cleanupExecutionQueue(moves, today = '') {
  return (moves || []).map((a) => {
    if (ACTION_CLOSED_STATUSES.includes(a?.status)) return a;
    const text = String(a.text || '');
    if (RECOGNIZED_VIRIAL.test(text) || RECOGNIZED_VIRIAL.test(String(a.linkedLabel || ''))) {
      return closeRecognized(a, today, 'stopped', 'Out of scope — frozen. Not a Blocks Estate / Bridge45 commercial move.');
    }
    if (a.workPackageId === 'wp-clock' || (isRecognizedSeedMove(a) && RECOGNIZED_CLOCK.test(text))) {
      return closeRecognized(a, today, 'learning', 'Sprint clock is already initialized. Closed as governance complete.');
    }
    if (
      a.workPackageId === 'wp-universe'
      || (isRecognizedSeedMove(a) && RECOGNIZED_UNIVERSE.test(text))
      || RECOGNIZED_UNIVERSE.test(text)
    ) {
      return closeRecognized(a, today, 'stopped', 'Universe build is Pipeline progress — not a single founder Today move.');
    }
    if (a.workPackageId === 'wp-scope-cleanup' && isRecognizedSeedMove(a)) {
      return closeRecognized(a, today, 'learning', 'Scope cleanup is governor machinery, not a daily founder move.');
    }
    return a;
  });
}

function applySelectionGuardrails(ranked) {
  const out = [];
  let internal = 0;
  for (const a of ranked) {
    if (out.length >= 3) break;
    const isInternal = a.expectedEvidence === 'OPS' || a.evidenceCredit === 'none';
    if (isInternal) {
      if (internal >= 1) continue;
      internal += 1;
    }
    out.push(a);
  }
  const proof = out.filter((a) => evidenceWeight(a.expectedEvidence) >= 1);
  if (proof.length < 2) {
    for (const a of ranked) {
      if (out.length >= 3) break;
      if (out.some((x) => x.id === a.id)) continue;
      if (evidenceWeight(a.expectedEvidence) >= 1) out.push(a);
    }
  }
  return out.slice(0, 3);
}

export function commercialCollectionPath(kind) {
  if (kind === 'opportunity') return 'opportunities';
  if (kind === 'partner') return 'd001.partners';
  if (kind === 'asset') return 'assetCandidates';
  if (kind === 'account') return 'accounts';
  return '';
}

export function closeAsWaitingExternal(move, { waitingUntil, waitingFor, today } = {}) {
  return {
    moveChanges: {
      status: 'Closed',
      disposition: 'commercial',
      selectedForToday: false,
      todayRank: 0,
      closedAt: today,
    },
    commercialChanges: {
      waitingExternal: true,
      waitingUntil: waitingUntil || '',
      waitingFor: waitingFor || move.text,
    },
    followUp: newAction(
      {
        text: `Follow up: ${waitingFor || move.linkedLabel || move.text}`,
        workstream: move.workstream,
        priority: move.priority || 'High',
        expectedEvidence: move.expectedEvidence,
        dueDate: waitingUntil || today,
        owner: 'Founder',
        status: 'Ready',
        source: 'Rule',
        linkedKind: move.linkedKind,
        linkedId: move.linkedId,
        linkedLabel: move.linkedLabel,
        workPackageId: move.workPackageId,
        epicId: move.epicId,
        gateRelevance: move.gateRelevance,
        whyNow: `Linked commercial object is waiting externally until ${waitingUntil || 'a dated reply'}.`,
      },
      today,
    ),
  };
}

function sortBacklog(items, ctx, key) {
  const copy = [...items];
  const dir = key?.startsWith('-') ? -1 : 1;
  const field = (key || 'priority').replace(/^-/, '');
  copy.sort((a, b) => {
    if (field === 'priority') {
      return dir * ((PRIORITY_SCORE[b.priority] || 0) - (PRIORITY_SCORE[a.priority] || 0) || scoreAction(b, ctx) - scoreAction(a, ctx));
    }
    if (field === 'dueDate') {
      const ad = a.dueDate || '9999-99-99';
      const bd = b.dueDate || '9999-99-99';
      return dir * ad.localeCompare(bd);
    }
    if (field === 'evidence') {
      return dir * (evidenceWeight(b.expectedEvidence) - evidenceWeight(a.expectedEvidence));
    }
    if (field === 'linked') {
      return dir * String(a.linkedLabel || a.linkedKind || '').localeCompare(String(b.linkedLabel || b.linkedKind || ''));
    }
    if (field === 'gate') {
      const ag = a.gateRelevance === 'none' ? 99 : Number(a.gateRelevance);
      const bg = b.gateRelevance === 'none' ? 99 : Number(b.gateRelevance);
      return dir * (ag - bg);
    }
    return dir * (scoreAction(b, ctx) - scoreAction(a, ctx));
  });
  return copy;
}

export function gateActionReview(all, gateDay, sprintOrToday) {
  const day = String(gateDay);
  const relevant = all.filter((a) => a.status !== 'Cancelled' && String(a.gateRelevance) === day);
  const open = relevant.filter(isOpenAction);
  const completed = relevant.filter((a) => a.status === 'Completed' || a.status === 'Closed');
  const asOf =
    typeof sprintOrToday === 'string'
      ? sprintOrToday
      : sprintOrToday?.executionAsOf || null;
  const overdue = asOf
    ? open.filter((a) => a.dueDate && isAfter(asOf, a.dueDate))
    : [];
  const blocked = open.filter((a) => a.status === 'Blocked');
  const evidence = completed.filter((a) => evidenceWeight(a.evidenceCreated) >= 3);
  const missing = open.filter((a) => a.priority === 'Critical' || a.priority === 'High');
  return {
    required: relevant,
    completed,
    overdue,
    blocked,
    evidenceCreated: evidence,
    missing,
  };
}

function e3e4Opportunities(state) {
  const opps = (state.opportunities || []).filter((o) => o.stage !== 'Won / Lost / Parked');
  return opps.filter((o) => evidenceWeight(o.evidenceLevel) >= 3);
}

export function opportunitiesMissingNextAction(state, actions, sprintOrToday) {
  const open = (actions || []).filter(isOpenAction);
  const missing = [];
  const asOf =
    typeof sprintOrToday === 'string'
      ? sprintOrToday
      : sprintOrToday?.executionAsOf || null;
  for (const o of e3e4Opportunities(state)) {
    const linked = open.find((a) => a.linkedKind === 'opportunity' && a.linkedId === o.id && a.dueDate);
    const onRecord = String(o.nextAction || '').trim() && o.nextActionDue;
    const overdue = asOf && o.nextActionDue && isAfter(asOf, o.nextActionDue) && !linked;
    if (!linked && (!onRecord || overdue)) {
      missing.push(o);
    }
  }
  return missing;
}

export function lowValueActivity(closed) {
  const completed = closed.filter((a) => a.status === 'Completed' || a.status === 'Closed');
  const empty = completed.filter((a) => {
    // Preparation / Bridge-created / OPS setup never counts as failed commercial proof.
    if (a.preparation || a.bridgeCreated || a.source === 'Bridge') return false;
    if (a.expectedEvidence === 'OPS' || a.evidenceCredit === 'none') return false;
    const result = a.completionResult || a.outcome;
    const created = evidenceWeight(a.evidenceCreated);
    return result === 'no' || result === 'operational' || (a.disposition === 'learning' && !created) || (result && result !== 'yes' && created < 1);
  });
  return empty.length >= 3 ? empty : [];
}

export function deriveActions(state, sprint, ctx = {}) {
  const raw = (state.top3 || []).map(migrateAction);
  const open = raw.filter(isOpenAction);
  const closed = raw.filter(isClosedAction);
  const scoredCtx = {
    sprint,
    d001: ctx.d001,
    funnel: ctx.funnel,
    time: ctx.time,
    exceptions: state.exceptions,
    state,
    constraintTitle: ctx.d001?.constraint?.blocker,
  };
  const today = pickToday(open, scoredCtx);
  const overdue = sprint.executionAsOf
    ? open.filter(
        (a) =>
          a.dueDate &&
          isAfter(sprint.executionAsOf, a.dueDate) &&
          (a.priority === 'Critical' || a.priority === 'High'),
      )
    : [];
  const blockedUnnamed = open.filter((a) => a.status === 'Blocked' && !String(a.blocker || '').trim());
  const converted = closed.filter((a) => (a.status === 'Completed' || a.status === 'Closed') && (a.completionResult === 'yes' || a.outcome === 'yes')).length;
  const graded = closed.filter((a) => (a.status === 'Completed' || a.status === 'Closed') && (a.completionResult || a.outcome)).length;
  const waiting = open.filter((a) => !today.some((t) => t.id === a.id));
  const nextGate = sprint.nextGate?.day;
  const forNextGate = open.filter((a) => nextGate && String(a.gateRelevance) === String(nextGate));
  const producing = closed.filter((a) => (a.status === 'Completed' || a.status === 'Closed') && evidenceWeight(a.evidenceCreated) >= 3);
  const lowValue = lowValueActivity(closed);
  const missingOppActions = opportunitiesMissingNextAction(state, raw, sprint);
  const initialization = open.filter((a) => isInitializationMove(a, state) && a.status !== 'Blocked');
  const readyNext = rankReadyNext(open, scoredCtx, 5);
  const waitingExternal = waitingExternalObjects(state);
  const activeWorkPackages = (state.workPackages || []).filter((p) => p.status !== 'Done' && p.status !== 'Dropped' && p.status !== 'Complete' && p.status !== 'Completed');

  return {
    all: raw,
    open,
    closed,
    backlog: sortBacklog(open, scoredCtx, 'priority'),
    today,
    readyNext,
    waitingExternal,
    initialization,
    activeWorkPackages,
    waiting,
    overdue,
    blocked: open.filter((a) => a.status === 'Blocked'),
    blockedUnnamed,
    history: [...closed].sort((a, b) => String(b.closedAt || b.completedAt || b.date || '').localeCompare(String(a.closedAt || a.completedAt || a.date || ''))),
    conversion: graded > 0 ? Math.round((converted / graded) * 100) : null,
    graded,
    forNextGate,
    producing,
    lowValue,
    missingOppActions,
    gateReview: {
      15: gateActionReview(raw, 15, sprint),
      30: gateActionReview(raw, 30, sprint),
      45: gateActionReview(raw, 45, sprint),
    },
    // Compatibility with the previous deriveTop3 shape.
    items: today,
    yesterday: [],
    openPast: waiting,
  };
}

export function sortActions(items, ctx, key) {
  return sortBacklog(items, ctx, key);
}

/**
 * Overdue is an execution concept. Pass the sprint object (preferred) or a
 * legacy ISO today string (treated as executionAsOf — no pre-sprint guard).
 */
export function isOverdue(a, sprintOrToday) {
  if (!isOpenAction(a) || !a.dueDate) return false;
  if (!sprintOrToday) return false;
  if (typeof sprintOrToday === 'string') {
    return isAfter(sprintOrToday, a.dueDate);
  }
  if (!sprintOrToday.executionAsOf) return false;
  return isAfter(sprintOrToday.executionAsOf, a.dueDate);
}

export function isDueToday(a, today) {
  return isOpenAction(a) && a.dueDate === today;
}
